import { Box, Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { FormikValues, useFormik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../components/CustomSelect";
import CustomSelectScroll from "@/components/CustomSelectScroll";
import CustomTimePicker from "../../../../../components/CustomTimePicker";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import ProfileViewer from "../../../../../components/ProfileViewer";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import StyledDialog from "../../../../../components/StyledDialog";
import TimeSlotPicker from "../../../../../components/TimeSlotPicker";
import ValueSwitcher from "../../../../../components/ValueSwitcher";
import { NEW_APPOINTMENT_VISIT_TYPE } from "../../../../../constants/scheduling";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useAppointmentTypeGetAll } from "../../../../../queries/practice-setting/appointment-type-configuration/use-appoinment-type-get-all";
import { useGetAppointment } from "../../../../../queries/use-get-appointment";
import { useLocationsList } from "../../../../../queries/use-locationList";
import { useRescheduleAppointment } from "../../../../../queries/use-reschedule-appointment";
import { formatTime } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";

interface PatientPortalRescheduleAppointmentModalProps {
  open: boolean;
  onClose: () => void;
  appointmentData?: any;
}

interface RescheduleFormValues extends FormikValues {
  uuid: string;
  mode: "IN_PERSON" | "VIRTUAL";
  locationId: string;
  startTime: string;
  endTime: string;
  duration: string;
  chiefComplaint: string;
}

const DEFAULT_VALUES: RescheduleFormValues = {
  uuid: "",
  mode: "IN_PERSON",
  locationId: "",
  startTime: "",
  endTime: "",
  duration: "",
  chiefComplaint: "",
};

const getMode = (appointmentMode?: string): "IN_PERSON" | "VIRTUAL" => {
  switch (appointmentMode) {
    case "In-Person":
    case "IN_PERSON":
    case "IN-PERSON":
    case "IN PERSON":
    case "In Person":
      return "IN_PERSON";
    default:
      return "VIRTUAL";
  }
};

const PatientPortalRescheduleAppointmentModal = ({
  open,
  onClose,
  appointmentData,
}: PatientPortalRescheduleAppointmentModalProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: rescheduleAppointment } = useRescheduleAppointment();
  const isTouched = useRef<boolean>(false);

  const { data: appointmentByIdData } = useGetAppointment({
    appointmentUuid: appointmentData?.uuid,
    providerGroupId: getXtenantId() ?? "",
    disabled: !open || !appointmentData?.uuid,
  });

  const providerUuid =
    appointmentData?.providerUuid ?? appointmentData?.providerId ?? "";

  const appointmentTypeSearch =
    appointmentData?.type ?? appointmentData?.appointmentType ?? "";

  const appointmentTypeUuid =
    appointmentData?.appointmentTypeUuid ??
    appointmentData?.typeUuid ??
    appointmentData?.appointmentTypeId;

  const initialStart = dayjs(new Date());
  // appointmentData?.startTime
  // ? dayjs(appointmentData?.startTime)

  const [openDateCalendar, setOpenDateCalendar] = useState<boolean>(false);
  const [showAvailability, setShowAvailability] = useState<boolean>(false);

  const [selectedDateTime, setSelectedDateTime] = useState<dayjs.Dayjs | null>(
    initialStart
  );

  const [searchLocationKey, setSearchLocationKey] = useDebounce<string>("");
  const [locationPagination, setLocationPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const { data: providerGroupLocationList, isFetching: locationFetching } =
    useLocationsList({
      pagination: locationPagination,
      searchKey: searchLocationKey,
      providerGroupId: getXtenantId(),
    });

  const { data: appointmentTypeData } = useAppointmentTypeGetAll({
    // pagination: DEF_PAGINATION,
    searchString: appointmentTypeSearch,
    status: true,
    archive: false,
    xTENANTID: getXtenantId(),
  });

  const selectedAppointmentTypeUuid = useMemo(() => {
    if (appointmentTypeUuid) {
      return appointmentTypeUuid;
    }

    return appointmentTypeData?.data?.content?.[0]?.uuid ?? "";
  }, [appointmentTypeData?.data?.content, appointmentTypeUuid]);

  const formik = useFormik<RescheduleFormValues>({
    initialValues: DEFAULT_VALUES,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.startTime || !values.endTime || !values.duration) {
        showSnackbar({
          message: "Please select an available slot to continue",
          type: "error",
        });
        return;
      }

      if (!values.chiefComplaint?.trim()) {
        showSnackbar({
          message: "Reason for visit is required",
          type: "error",
        });
        return;
      }

      const appointmentDetails = appointmentByIdData?.data ?? appointmentData;

      const payload = {
        ...appointmentDetails,
        ...values,
        uuid: appointmentData?.uuid,
        locationId: values.mode === "IN_PERSON" ? values.locationId : undefined,
        startTime: values.startTime,
        endTime: values.endTime,
        duration: Number(values.duration),
        chiefComplaint: values.chiefComplaint?.trim(),
        appointmentRequest: true,
      };

      try {
        const response = await rescheduleAppointment({
          rescheduleAppointment: payload,
          xTENANTID: getXtenantId(),
        });

        queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
        showSnackbar(getSuccessSnackbar(response));
        handleClose();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    const selectedMode = getMode(
      appointmentData?.appointmentMode ?? appointmentData?.mode
    );

    const duration = appointmentData?.duration
      ? String(appointmentData?.duration)
      : "";

    formik.setValues({
      uuid: appointmentData?.uuid ?? "",
      mode: selectedMode,
      locationId: appointmentData?.locationId ?? "",
      startTime: appointmentData?.startTime
        ? new Date(appointmentData.startTime).toISOString()
        : "",
      endTime: appointmentData?.endTime
        ? new Date(appointmentData.endTime).toISOString()
        : "",
      duration,
      chiefComplaint:
        appointmentData?.chiefComplaint ?? appointmentData?.reason ?? "",
    });

    setSelectedDateTime(dayjs(new Date()));
    setOpenDateCalendar(!!appointmentData?.startTime);
    isTouched.current = false;
  }, [appointmentData, open]);

  const providerName = useMemo(() => {
    if (appointmentData?.providerName) {
      return appointmentData.providerName;
    }

    return [
      appointmentData?.providerFirstName,
      appointmentData?.providerLastName,
    ]
      .filter(Boolean)
      .join(" ");
  }, [
    appointmentData?.providerFirstName,
    appointmentData?.providerLastName,
    appointmentData?.providerName,
  ]);

  const providerRole =
    appointmentData?.providerRole ??
    appointmentData?.providerSpecialities ??
    appointmentData?.specialty ??
    "-";

  const appointmentStatus =
    appointmentData?.appointmentStatus ?? appointmentData?.status ?? "-";

  const appointmentType =
    appointmentData?.type ?? appointmentData?.appointmentType ?? "-";

  function handleClose() {
    formik.resetForm();
    onClose();
  }

  function toggleDateCalendar() {
    setOpenDateCalendar((prev) => !prev);
    setShowAvailability((prev) => !prev);
  }

  return (
    <SideSheetDrawer
      open={open}
      onClose={handleClose}
      title="Reschedule Appointment"
      w="760px"
      footerActions={
        <>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => formik.handleSubmit()}>
            Reschedule
          </Button>
        </>
      }
    >
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          p: 2,
          backgroundColor: "#F5F7F9",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ProfileViewer
              withoutColn={false}
              firstColW={5}
              secondColW={7}
              data={{
                "Provider Name": providerName || "-",
                "Appointment Status": appointmentStatus,
                Authorization: appointmentData?.authorization || "-",
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <ProfileViewer
              withoutColn={false}
              firstColW={5}
              secondColW={7}
              data={{
                Speciality: providerRole,
                "Appointment Type": appointmentType,
                "Visit Type":
                  formik.values.mode === "IN_PERSON"
                    ? "In Person"
                    : "Telehealth",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={4}>
          <Flex alignItems="center" gap={1} sx={{ minHeight: 40 }}>
            <span>Visit Type:</span>
            <ValueSwitcher
              alignment={formik.values.mode}
              handleChange={(_, value) => {
                formik.setFieldValue("mode", value as "IN_PERSON" | "VIRTUAL");
                if (value === "VIRTUAL") {
                  formik.setFieldValue("locationId", "");
                }
              }}
              timing={NEW_APPOINTMENT_VISIT_TYPE}
            />
          </Flex>
        </Grid>

        <Grid item xs={12} sm={8}>
          <CustomSelectScroll
            required={formik.values.mode === "IN_PERSON"}
            label="Location"
            loading={locationFetching}
            options={providerGroupLocationList?.data?.content?.map((it) => ({
              value: it?.uuid,
              label: it?.name,
            }))}
            placeholder="Select"
            value={formik.values.locationId}
            onSearch={(_, val) => {
              setSearchLocationKey(val);
              setLocationPagination((p) => ({ ...p, page: 0 }));
            }}
            pagination={locationPagination}
            setPagination={setLocationPagination}
            totalElements={providerGroupLocationList?.data?.page?.totalElements}
            onChange={(value) => formik.setFieldValue("locationId", value)}
            readOnly={formik.values.mode !== "IN_PERSON"}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <CustomSelect
            label="Speciality"
            value={providerRole}
            readOnly
            options={[{ value: providerRole, label: providerRole }]}
          />
        </Grid>

        <Grid item xs={12} sm={5}>
          <CustomSelect
            label="Provider"
            value={providerName}
            readOnly
            options={[{ value: providerName, label: providerName }]}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <ErrorHelper
            error={
              isTouched.current && !formik.values.duration
                ? "Please Select Slot"
                : undefined
            }
          >
            <Button
              fullWidth
              variant="outlined"
              sx={{ height: 40 }}
              onClick={() => {
                setShowAvailability(true);
                setOpenDateCalendar(true);
                isTouched.current = !formik.values.duration;
              }}
            >
              View Availability
            </Button>
          </ErrorHelper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <CustomDatePicker
            disabled
            onChange={() => undefined}
            label="Date"
            value={formik.values.duration ? selectedDateTime : undefined}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <CustomTimePicker
            disabled
            label="Start Time"
            valueFormatter="hh:mm A"
            value={
              formik.values.startTime && formik.values.duration
                ? formatTime(new Date(formik.values.startTime))
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <CustomSelect
            readOnly
            label="Duration"
            value={formik.values.duration}
            options={
              formik.values.duration
                ? [
                    {
                      value: formik.values.duration,
                      label: formik.values.duration,
                    },
                  ]
                : undefined
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Input
            label="Reason For Visit"
            required
            textFieldProps={{
              name: "chiefComplaint",
              placeholder: "Reason",
              value: formik.values.chiefComplaint,
              onChange: formik.handleChange,
            }}
            error={
              !formik.values.chiefComplaint?.trim() && isTouched.current
                ? "Reason for visit is required"
                : undefined
            }
          />
        </Grid>
      </Grid>

      <StyledDialog
        open={showAvailability}
        onClose={() => setShowAvailability(false)}
      >
        <Grid item xs={12}>
          <Box width="45rem">
            <TimeSlotPicker
              bookedSlot={appointmentData?.startTime}
              isTouched={isTouched}
              formik={formik}
              appointmentType={selectedAppointmentTypeUuid}
              mode={formik.values.mode}
              providerUuid={providerUuid}
              selectedLocation={
                formik.values.mode === "VIRTUAL"
                  ? undefined
                  : formik.values.locationId
              }
              isRescheduled
              onChange={(value) => {
                setSelectedDateTime(value as dayjs.Dayjs);
              }}
              selectedDateTime={selectedDateTime}
              openDateCalendar={openDateCalendar}
              toggleDateCalendar={toggleDateCalendar}
              type="rescheduled"
            />
          </Box>
        </Grid>
      </StyledDialog>
    </SideSheetDrawer>
  );
};

export default PatientPortalRescheduleAppointmentModal;
